import PublicApiService from "../../../services/PublicApiService";
import DeviceCacheHelper from "../../devices/addDevice/helpers/deviceCacheHelper/DeviceCacheHelper";
import { FREYA_BADGE_FETCH_TIMEOUT } from "../../../common/constants";
import { AxiosError } from "axios";
import { HttpErrorStatus } from "../../../common/enums";
import { useRef } from "react";
import { useDispatch, useStore } from "react-redux";
import { RootState } from "../../../redux/store";
import dayjs from "../../../common/dayjs";
import { EncryptedBadge, GetFreyaBadgesResponse } from "../../../redux/types";
import FreyaStorage from "../../../common/freyaStorage/FreyaStorage";
import { addOrUpdateBadgesArray } from "./utils/utils";
import FeatureToggles from "../../../featureToggles/featureToggles";
import {
  setLastBadgeUpdateTime,
  badgeSyncRequestStarted,
  badgeSyncRequestCompleted,
  badgeSyncRequestFailed
} from "../../../redux/metadata/slice/metadataSlice";
import GlobalErrorHandler from "../../../common/utils/errorHandlers/GlobalErrorHandler";

const errorHandler = GlobalErrorHandler.getInstance();
let badgeFetchInterval: NodeJS.Timeout | undefined;

export const useGetFreyaBadges = () => {
  const dispatch = useDispatch();
  const { getState } = useStore();
  const badges = useRef<EncryptedBadge[]>([]);
  const badRequestCounter = useRef(0);

  const fetchBadgesForGbsCode = async (deviceId: string, gbsCode: string, lastModifiedDate: string) => {
    const isNewEndpointUsed = FeatureToggles.isFeatureEnabled(FeatureToggles.keys.SHORTENED_FREYA_BADGE_ENDPOINT);
    let response: GetFreyaBadgesResponse;

    if (isNewEndpointUsed) {
      response = await PublicApiService.getFreyaBadgesShortened(deviceId, gbsCode, "", lastModifiedDate);
    } else {
      response = await PublicApiService.getFreyaBadges(deviceId, gbsCode, "", lastModifiedDate);
    }

    const fetchedBadges: EncryptedBadge[] = [...response.results];

    while (response.token) {
      if (isNewEndpointUsed) {
        response = await PublicApiService.getFreyaBadgesShortened(deviceId, gbsCode, response.token, lastModifiedDate);
      } else {
        response = await PublicApiService.getFreyaBadges(deviceId, gbsCode, response.token, lastModifiedDate);
      }
      fetchedBadges.push(...response.results);
    }

    return fetchedBadges;
  };

  const handleLogout = async () => {
    // TODO: handle log out
    await DeviceCacheHelper.resetCache();
    clearBadgeFetchInterval();
    window.location.reload();
  };

  const updateBadges = async (badges: EncryptedBadge[]) => {
    let currentBadges: EncryptedBadge[] = await FreyaStorage.getBadges();
    const updatedArray = addOrUpdateBadgesArray(badges, currentBadges);
    await FreyaStorage.setBadges(updatedArray);
  };

  const fetchEncryptedBadgeById = async (badgeId: string) => {
    dispatch(badgeSyncRequestStarted());
    try {
      const deviceId = await DeviceCacheHelper.getCachedInstanceId();
      const encryptedBadge = await PublicApiService.getEncryptedFreyaBadgeById(deviceId, badgeId);
      await updateBadges([encryptedBadge]);
      dispatch(badgeSyncRequestCompleted());
    } catch (e) {
      dispatch(badgeSyncRequestFailed());
      console.error(e);
    }
  };

  const fetchLatestBadgeQrCode = async (qrCodeContent: string) => {
    const deviceId = await DeviceCacheHelper.getCachedInstanceId();
    return PublicApiService.getLatestFreyaBadgeQrCode(deviceId, qrCodeContent);
  };

  const fetchBadges = async (gbsCodeOrCodes: string | string[], lastModifiedDate = "") => {
    dispatch(badgeSyncRequestStarted());
    try {
      const deviceId = await DeviceCacheHelper.getCachedInstanceId();
      const gbsCodes = Array.from(new Set((Array.isArray(gbsCodeOrCodes) ? gbsCodeOrCodes : [gbsCodeOrCodes]).filter(Boolean)));
      badges.current = [];

      for (const gbsCode of gbsCodes) {
        const fetched = await fetchBadgesForGbsCode(deviceId, gbsCode, lastModifiedDate);
        badges.current = badges.current.concat(fetched);
      }

      await updateBadges(badges.current);
      dispatch(setLastBadgeUpdateTime(dayjs(new Date()).format()));
      dispatch(badgeSyncRequestCompleted());
    } catch (e) {
      dispatch(badgeSyncRequestFailed());

      if ((e as AxiosError).response?.status === HttpErrorStatus.BAD_REQUEST) {
        console.log("FREYA LOG: BAD REQUEST", (e as AxiosError).response?.status);
        if (badRequestCounter.current >= 4) {
          throw e;
        }
        badges.current = [];
        badRequestCounter.current += 1;
        await fetchBadges(gbsCodeOrCodes, lastModifiedDate);
        return;
      }
      if ((e as AxiosError).response?.status === HttpErrorStatus.UNAUTHORIZED) {
        handleLogout();
        throw e;
      }

      throw e;
    }
  };

  const startBadgeFetchInterval = (gbsCodeOrCodes: string | string[]) => {
    if (badgeFetchInterval) {
      clearInterval(badgeFetchInterval);
    }
    badgeFetchInterval = setInterval(() => {
      const date = (getState() as RootState).metadata.lastBadgeUpdateTime;
      fetchBadges(gbsCodeOrCodes, dayjs(date).utc().format());
    }, FREYA_BADGE_FETCH_TIMEOUT);
  };

  const clearBadgeFetchInterval = () => {
    if (badgeFetchInterval) {
      clearInterval(badgeFetchInterval);
      badgeFetchInterval = undefined;
    }
  };

  const getFreyaBadges = async () => {
    dispatch(badgeSyncRequestStarted());
    try {
      const badges = await FreyaStorage.getBadges();
      dispatch(badgeSyncRequestCompleted());
      return badges;
    } catch (e) {
      dispatch(badgeSyncRequestFailed());
      console.error("Error getting Freya badges:", e);
      errorHandler.handlePrintServerError(e as Error, "getFreyaBadges");
      return [];
    }
  };

  return {
    fetchBadges,
    startBadgeFetchInterval,
    clearBadgeFetchInterval,
    getFreyaBadges,
    fetchEncryptedBadgeById,
    fetchLatestBadgeQrCode
  };
};
